import { useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { NumberHelper } from 'core/helpers';
import { FormProvider } from 'core/states';
import { PricingProfilePartNumberType } from 'pricing/enums';
import { usePartNumberActions } from 'pricing/hooks';
import { IPricingProfilePartNumber, IPricingProfilePartNumberForm } from 'pricing/types';

import { MARGIN_DECIMALS } from './helpers';
import { upsertPricingProfilePartNumberSchema } from './schema';
import { UpsertPricingProfilePartNumberFormFields } from './UpsertPricingProfilePartNumberFormFields';
import { mapUpsertPricingProfilePartNumber } from './utils';

interface IProps {
  pricingProfilePartNumber?: IPricingProfilePartNumber;
  structureId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfilePartNumber = ({
  pricingProfilePartNumber,
  structureId,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const { upsertPartNumber } = usePartNumberActions();
  const { user } = useAuthUser();

  // Pricing profile costs never reach an alternate-version user's quotes, so the cost section
  // is hidden and its rules skipped for them; their stored costs still round-trip untouched.
  const withCosts = !user?.alternateVersionEnabled;

  const defaultValues = useMemo(() => {
    return {
      part: pricingProfilePartNumber
        ? {
            number: pricingProfilePartNumber.part,
            manufacturerId: pricingProfilePartNumber.manufacturerId,
          }
        : null,
      uom: pricingProfilePartNumber?.uom,
      type: pricingProfilePartNumber?.margin
        ? PricingProfilePartNumberType.MARGIN
        : PricingProfilePartNumberType.PRICE,
      price: pricingProfilePartNumber?.price,
      // The margin is stored as a fraction and edited as a percentage. It has to stay a raw number:
      // a formatted default ("25.00%") casts back to null, so saving an untouched margin row would
      // wipe the margin.
      margin: pricingProfilePartNumber?.margin
        ? NumberHelper.round(pricingProfilePartNumber.margin * 100, MARGIN_DECIMALS)
        : undefined,
      purchasedCost: pricingProfilePartNumber?.purchasedCost,
      cost: pricingProfilePartNumber?.cost,
    };
  }, [pricingProfilePartNumber]);

  const onSubmit = () => async (data: IPricingProfilePartNumberForm) => {
    const { success } = await upsertPartNumber(
      mapUpsertPricingProfilePartNumber(structureId, data, pricingProfilePartNumber)
    );

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess();
      onClose();
    }, 1000);
  };

  const handleOnRemoveSuccess = () => {
    setTimeout(() => {
      onRemoveSuccess?.();
      onClose();
    }, 1000);
  };

  return (
    <FormProvider
      schema={upsertPricingProfilePartNumberSchema({ withCosts })}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPricingProfilePartNumberFormFields
        pricingProfilePartNumber={pricingProfilePartNumber}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        onRemoveSuccess={handleOnRemoveSuccess}
      />
    </FormProvider>
  );
};
